// A message refused because the agent's computer was restarting is held and re-sent
// (same client id: one turn, one DM row). Only refusals that PROVE it never reached the
// model are re-sent; a turn that may have run gets friendly copy and a manual retry.

/** Mirrors the server's AGENT_RESTARTING_CODE (cyborg/agent-restarting-failure.ts). */
export const AGENT_RESTARTING_CODE = "daemon_restarting";

// The relay's offline-owner refusal, and older daemons' uncoded registration refusals.
// ponytail: a second spelling of server text (ui cannot import server); the code is the contract.
const NEVER_DELIVERED_RE =
  /^The computer that runs this agent is offline|Agent manager is shutting down|^Agent manager not initialized/i;

// "The computer went away" without proof the turn never ran: friendly copy, no re-send.
const UNREACHABLE_RE =
  /ECONNREFUSED|ECONNRESET|socket hang up|socket (?:has been |was )?closed|disconnected before answering|daemon (?:is )?not running/i;

/** The message never reached the model: safe to send again under the same client id. */
export function isNeverDeliveredFailure(event: { error?: string; code?: string }): boolean {
  return event.code === AGENT_RESTARTING_CODE || NEVER_DELIVERED_RE.test(event.error ?? "");
}

/** Any "the agent's computer could not be reached" failure, for the friendly card. */
export function isAgentUnreachableFailure(event: { error?: string; code?: string }): boolean {
  return isNeverDeliveredFailure(event) || UNREACHABLE_RE.test(event.error ?? "");
}

export const REDELIVERY_WINDOW_MS = 90_000;
const FIRST_RETRY_MS = 2_000;
const MAX_RETRY_GAP_MS = 15_000;

/** `book` schedules a prompt's next re-send (backoff, one pending at a time), or answers
 * false once its window — counted from the FIRST refusal — is spent. */
export class RedeliveryHolds {
  private readonly holds = new Map<string, { deadline: number; attempts: number }>();
  private readonly booked = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(private readonly windowMs = REDELIVERY_WINDOW_MS) {}

  book(key: string, now: number, resend: () => void): boolean {
    if (this.booked.has(key)) return true;
    // ponytail: pruned on use, one window late, so a late refusal cannot reopen a window.
    for (const [k, h] of this.holds) if (h.deadline + this.windowMs < now) this.holds.delete(k);
    const hold = this.holds.get(key) ?? { deadline: now + this.windowMs, attempts: 0 };
    if (now >= hold.deadline) return false;
    hold.attempts += 1;
    this.holds.set(key, hold);
    const gap = Math.min(FIRST_RETRY_MS * 2 ** (hold.attempts - 1), MAX_RETRY_GAP_MS);
    const timer = setTimeout(
      () => {
        this.booked.delete(key);
        resend();
      },
      Math.min(gap, hold.deadline - now),
    );
    this.booked.set(key, timer);
    return true;
  }

  /** Drop every hold and cancel every pending re-send (account switch / sign-out). */
  clear(): void {
    for (const timer of this.booked.values()) clearTimeout(timer);
    this.booked.clear();
    this.holds.clear();
  }

  get size(): number {
    return this.holds.size;
  }

  /** Inside its window: a refusal of a re-send is the hold's to handle, not a failure. */
  isHeld(key: string, now: number): boolean {
    const hold = this.holds.get(key);
    return hold !== undefined && now < hold.deadline;
  }
}

export function redeliveryKey(agentId: string, entryId: string): string {
  return `${agentId}\u0000${entryId}`;
}
